import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Table, Typography, Space, Select, DatePicker, Progress, Empty, Tag } from "antd";
import {
  EnvironmentOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  LabelList,
  ReferenceLine,
} from "recharts";
import { fetchWorkRouteAnalytics } from "../workRoutesThunk";

const { Title, Text } = Typography;

const COLOR = {
  blue: "#2563eb",
  green: "#16a34a",
  amber: "#f59e0b",
  red: "#ef4444",
  violet: "#7c3aed",
  slate: "#64748b",
  ink: "#0f172a",
};

// Route-point session statuses -> friendly label + colour.
const STATUS_META = {
  checked_out: { label: "Completed", color: COLOR.green },
  checked_in: { label: "In progress", color: COLOR.blue },
  missed: { label: "Missed", color: COLOR.red },
  pending: { label: "Pending", color: COLOR.amber },
  skipped: { label: "Skipped", color: "#94a3b8" },
};
const FALLBACK_COLORS = [COLOR.violet, "#0ea5e9", "#14b8a6", "#ec4899"];

const titleCase = (s) =>
  String(s || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// 396 -> "6h 36m", 11.1 -> "11.1 min"
const formatMinutes = (value) => {
  const n = Number(value) || 0;
  if (n < 60) return `${Math.round(n * 10) / 10} min`;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const truncate = (s, max = 26) => {
  const str = String(s ?? "");
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

const cardStyle = {
  borderRadius: 12,
  border: "1px solid #eef0f4",
  boxShadow: "0 1px 3px rgba(15,23,42,0.05), 0 6px 16px rgba(15,23,42,0.04)",
  height: "100%",
};
const cardStyles = {
  header: { borderBottom: "none", minHeight: 52, fontSize: 15, fontWeight: 600, color: COLOR.ink },
  body: { padding: "0 20px 20px" },
};

const KpiCard = ({ icon, color, title, value, sub }) => (
  <Card style={{ ...cardStyle, borderTop: `3px solid ${color}` }} styles={{ body: { padding: "16px 18px" } }}>
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: `${color}1A`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: COLOR.slate, fontSize: 13 }}>{title}</div>
        <div style={{ color: COLOR.ink, fontSize: 26, fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
        {sub && <div style={{ color, fontSize: 12, fontWeight: 500 }}>{sub}</div>}
      </div>
    </div>
  </Card>
);

const NoData = ({ text = "No data for this period" }) => (
  <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text} />
  </div>
);

// Horizontal, sorted bars with value labels and an average marker. Bars that
// run 25% over the average are highlighted so slow spots stand out.
const DurationBarChart = ({ data, average, idPrefix, baseColors, highlightColors }) => {
  const height = Math.max(280, data.length * 36 + 60);
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={data} margin={{ top: 26, right: 70, left: 4, bottom: 4 }}>
          <defs>
            <linearGradient id={`${idPrefix}-base`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={baseColors[0]} />
              <stop offset="100%" stopColor={baseColors[1]} />
            </linearGradient>
            <linearGradient id={`${idPrefix}-high`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={highlightColors[0]} />
              <stop offset="100%" stopColor={highlightColors[1]} />
            </linearGradient>
          </defs>
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: COLOR.slate }}
            tickFormatter={(v) => (v >= 60 ? `${Math.round(v / 6) / 10}h` : `${v}m`)}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={190}
            tick={{ fontSize: 13, fill: "#334155" }}
            tickFormatter={(v) => truncate(v)}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(37,99,235,0.06)" }}
            formatter={(v) => [formatMinutes(v), "Average"]}
            contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 13 }}
          />
          {average > 0 && (
            <ReferenceLine
              x={average}
              stroke={COLOR.slate}
              strokeDasharray="5 4"
              label={{ value: `Avg ${formatMinutes(average)}`, position: "top", fill: COLOR.slate, fontSize: 12 }}
            />
          )}
          <Bar dataKey="avg_minutes" barSize={18} radius={[0, 8, 8, 0]}>
            {data.map((d) => (
              <Cell
                key={d.name}
                fill={`url(#${idPrefix}-${average > 0 && d.avg_minutes > average * 1.25 ? "high" : "base"})`}
              />
            ))}
            <LabelList
              dataKey="avg_minutes"
              position="right"
              formatter={formatMinutes}
              style={{ fontSize: 12, fontWeight: 600, fill: "#334155" }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: COLOR.slate }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: color, display: "inline-block" }} />
    {label}
  </span>
);

const Analytics = () => {
  const dispatch = useDispatch();
  const { analytics, loading } = useSelector((state) => state.workRoutes);

  const [periodType, setPeriodType] = useState("daily");
  const [selectedPeriod, setSelectedPeriod] = useState(dayjs());

  useEffect(() => {
    dispatch(
      fetchWorkRouteAnalytics({
        period_type: periodType,
        date: selectedPeriod.format("YYYY-MM-DD"),
      })
    );
  }, [dispatch, periodType, selectedPeriod]);

  const overview = useMemo(() => analytics?.overview || {}, [analytics]);
  const employeeStats = useMemo(() => analytics?.employee_stats || [], [analytics]);

  const total = Number(overview.total_visits) || 0;
  const completed = Number(overview.completed_visits) || 0;
  const active = Number(overview.active_visits) || 0;
  const missed = Number(overview.missed_visits) || 0;
  const pct = (n) => (total ? `${Math.round((n / total) * 100)}% of visits` : "—");

  const statusData = useMemo(
    () =>
      (analytics?.route_completion || []).map((row, i) => {
        const meta = STATUS_META[row.status] || {
          label: titleCase(row.status),
          color: FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        };
        return { ...meta, total: Number(row.total) || 0 };
      }),
    [analytics]
  );
  const statusTotal = statusData.reduce((sum, s) => sum + s.total, 0);

  const allVisitDurations = useMemo(
    () =>
      (analytics?.visit_durations || [])
        .map((d) => ({ name: d.name || "Unnamed point", avg_minutes: Number(d.avg_minutes) || 0 }))
        .sort((a, b) => b.avg_minutes - a.avg_minutes),
    [analytics]
  );
  const visitDurations = allVisitDurations.slice(0, 12);
  const visitAverage = allVisitDurations.length
    ? allVisitDurations.reduce((s, d) => s + d.avg_minutes, 0) / allVisitDurations.length
    : 0;

  const allRouteDurations = useMemo(
    () =>
      (analytics?.route_durations || [])
        .map((d) => ({ name: d.name || "Unnamed route", avg_minutes: Number(d.avg_minutes) || 0 }))
        .sort((a, b) => b.avg_minutes - a.avg_minutes),
    [analytics]
  );
  const routeDurations = allRouteDurations.slice(0, 10);
  const routeAverage = allRouteDurations.length
    ? allRouteDurations.reduce((s, d) => s + d.avg_minutes, 0) / allRouteDurations.length
    : 0;

  // Average visit length across all employees, weighted by how many visits each did.
  const weightedVisitMinutes = useMemo(() => {
    const withVisits = employeeStats.filter((e) => Number(e.visits) > 0);
    const visits = withVisits.reduce((s, e) => s + Number(e.visits), 0);
    return visits ? withVisits.reduce((s, e) => s + Number(e.avg_visit_minutes) * Number(e.visits), 0) / visits : 0;
  }, [employeeStats]);

  const maxVisits = Math.max(1, ...employeeStats.map((e) => Number(e.visits) || 0));

  const columns = [
    {
      title: "#",
      width: 56,
      render: (_, __, index) => index + 1,
      onCell: () => ({ "data-label": "Rank" }),
    },
    {
      title: "Employee",
      dataIndex: "employee_name",
      render: (v) => <Text strong>{v}</Text>,
      onCell: () => ({ "data-label": "Employee" }),
    },
    {
      title: "Visits",
      dataIndex: "visits",
      sorter: (a, b) => Number(a.visits) - Number(b.visits),
      render: (v) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 140 }}>
          <Progress
            percent={Math.round(((Number(v) || 0) / maxVisits) * 100)}
            showInfo={false}
            size="small"
            strokeColor={COLOR.blue}
            style={{ flex: 1, margin: 0 }}
          />
          <Text strong style={{ minWidth: 24, textAlign: "right" }}>{v}</Text>
        </div>
      ),
      onCell: () => ({ "data-label": "Visits" }),
    },
    {
      title: "Avg Visit",
      dataIndex: "avg_visit_minutes",
      sorter: (a, b) => Number(a.avg_visit_minutes) - Number(b.avg_visit_minutes),
      render: (v) => (Number(v) ? <Tag color="blue">{formatMinutes(v)}</Tag> : <Text type="secondary">—</Text>),
      onCell: () => ({ "data-label": "Avg Visit" }),
    },
    {
      title: "Avg Route",
      dataIndex: "avg_route_minutes",
      sorter: (a, b) => Number(a.avg_route_minutes) - Number(b.avg_route_minutes),
      render: (v) => (Number(v) ? <Tag color="green">{formatMinutes(v)}</Tag> : <Text type="secondary">—</Text>),
      onCell: () => ({ "data-label": "Avg Route" }),
    },
  ];

  const periodLabel =
    periodType === "monthly" ? selectedPeriod.format("MMMM YYYY") : selectedPeriod.format("DD MMM YYYY");

  return (
    <div style={{ padding: "4px" }}>
      {/* Card layout for the employee table on small screens */}
      <style>{`
        @media (max-width: 767px) {
          .analytics-table .ant-table-thead { display: none; }
          .analytics-table .ant-table-tbody > tr { display: block; border: 1px solid #f0f0f0; border-radius: 8px; margin-bottom: 12px; padding: 8px; background: #fff; }
          .analytics-table .ant-table-tbody > tr > td { display: flex; justify-content: space-between; align-items: center; border-bottom: none !important; padding: 6px 8px !important; text-align: right; }
          .analytics-table .ant-table-tbody > tr > td::before { content: attr(data-label); font-weight: 600; text-align: left; padding-right: 16px; font-size: 13px; color: rgba(0, 0, 0, 0.65); }
        }
      `}</style>

      <Row justify="space-between" align="middle" gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Work Route Analytics
          </Title>
          <Text type="secondary">
            {periodType === "monthly" ? "Monthly" : "Daily"} overview · {periodLabel}
          </Text>
        </Col>

        <Col>
          <Space wrap>
            <Select
              value={periodType}
              style={{ width: 130 }}
              onChange={(value) => {
                setPeriodType(value);
                setSelectedPeriod(dayjs());
              }}
              options={[
                { label: "Daily", value: "daily" },
                { label: "Monthly", value: "monthly" },
              ]}
            />
            <DatePicker
              value={selectedPeriod}
              picker={periodType === "monthly" ? "month" : "date"}
              onChange={(value) => value && setSelectedPeriod(value)}
              allowClear={false}
            />
          </Space>
        </Col>
      </Row>

      {/* KPIs — these count visits (check-ins at route points), not routes */}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={8} xl={4}>
          <KpiCard icon={<EnvironmentOutlined />} color={COLOR.blue} title="Total Visits" value={total} />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <KpiCard icon={<CheckCircleOutlined />} color={COLOR.green} title="Completed" value={completed} sub={pct(completed)} />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <KpiCard icon={<SyncOutlined />} color={COLOR.amber} title="In Progress" value={active} sub={pct(active)} />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <KpiCard icon={<ExclamationCircleOutlined />} color={COLOR.red} title="Missed" value={missed} sub={pct(missed)} />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <KpiCard
            icon={<ClockCircleOutlined />}
            color={COLOR.violet}
            title="Avg Visit Duration"
            value={weightedVisitMinutes ? formatMinutes(weightedVisitMinutes) : "—"}
          />
        </Col>
        <Col xs={12} md={8} xl={4}>
          <KpiCard
            icon={<FieldTimeOutlined />}
            color="#0ea5e9"
            title="Avg Route Duration"
            value={routeAverage ? formatMinutes(routeAverage) : "—"}
            sub={allRouteDurations.length ? `${allRouteDurations.length} route${allRouteDurations.length !== 1 ? "s" : ""}` : undefined}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={9}>
          <Card title="Visit Status" style={cardStyle} styles={cardStyles} loading={loading}>
            {statusTotal === 0 ? (
              <NoData text="No visits in this period" />
            ) : (
              <>
                <div style={{ position: "relative", width: "100%", height: 250 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        dataKey="total"
                        nameKey="label"
                        innerRadius="62%"
                        outerRadius="90%"
                        paddingAngle={statusData.length > 1 ? 3 : 0}
                        cornerRadius={6}
                        stroke="none"
                      >
                        {statusData.map((s) => (
                          <Cell key={s.label} fill={s.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, name) => [v, name]}
                        contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 13 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <span style={{ fontSize: 30, fontWeight: 700, color: COLOR.ink, lineHeight: 1 }}>{statusTotal}</span>
                    <span style={{ fontSize: 12, color: COLOR.slate }}>total visits</span>
                  </div>
                </div>

                <div style={{ marginTop: 8 }}>
                  {statusData.map((s) => (
                    <div
                      key={s.label}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderTop: "1px solid #f1f5f9" }}
                    >
                      <LegendDot color={s.color} label={<Text style={{ fontSize: 13 }}>{s.label}</Text>} />
                      <span>
                        <Text strong>{s.total}</Text>
                        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                          {Math.round((s.total / statusTotal) * 100)}%
                        </Text>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card
            title="Average Customer Visit Duration"
            extra={
              allVisitDurations.length > visitDurations.length ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Longest {visitDurations.length} of {allVisitDurations.length}
                </Text>
              ) : null
            }
            style={cardStyle}
            styles={cardStyles}
            loading={loading}
          >
            {visitDurations.length === 0 ? (
              <NoData text="No completed visits in this period" />
            ) : (
              <>
                <DurationBarChart
                  data={visitDurations}
                  average={visitAverage}
                  idPrefix="visit"
                  baseColors={["#60a5fa", COLOR.blue]}
                  highlightColors={["#fbbf24", "#ea580c"]}
                />
                <Space size={16} style={{ marginTop: 4 }}>
                  <LegendDot color={COLOR.blue} label="Within average" />
                  <LegendDot color={COLOR.amber} label="25%+ above average" />
                </Space>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={9}>
          <Card title="Average Route Duration" style={cardStyle} styles={cardStyles} loading={loading}>
            {routeDurations.length === 0 ? (
              <NoData text="No completed routes in this period" />
            ) : (
              <DurationBarChart
                data={routeDurations}
                average={routeDurations.length > 1 ? routeAverage : 0}
                idPrefix="route"
                baseColors={["#4ade80", COLOR.green]}
                highlightColors={["#fbbf24", "#ea580c"]}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card title="Employee Performance" style={cardStyle} styles={cardStyles} loading={loading}>
            <Table
              className="analytics-table"
              rowKey="employee_name"
              columns={columns}
              dataSource={employeeStats}
              pagination={{ size: "small", hideOnSinglePage: true, pageSize: 8 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
